import {
  DeliveryHighlightMessageUI,
  Props,
} from "./DeliveryHighlightMessageUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { isInStoreOnly } from "../../utils/variant/isInStoreOnly";
import { isAvailableToPreOrder } from "../../utils/variant/isAvailableToPreOrder";
import { isFutureRelease } from "../../utils/variant/isFutureRelease";
import { ensureIsActive } from "../../utils/product/ensureIsActive";
import { hasAnyStock } from "../../utils/product/hasAnyStock";
import { isInExtendedReturnsWindow } from "../../utils/product/isInExtendedReturnsWindow";
import { CLICK_AND_COLLECT } from "../ProductAccordion/components/ProductDeliveryOptionsAccordion/util/constants";
import { hasColours } from "../../utils/product/hasColours";
import { Variant } from "../../types/types";

const TWO_MAN_DELIVERY_DMS_ID = "26";
const FREE_DELIVERY_THRESHOLD = 50;
const FREE_CLICK_AND_COLLECT_THRESHOLD = 30;
const productTypesForRestrictedReturns = [
  "Earrings",
  "Jewellery Sets",
  "Knickers",
  "Pants",
  "Bikini Bottoms",
  "Bikini Sets",
  "Swimsuit",
  "Swim Bottoms",
  "Swim Briefs",
  "Swim Sets",
  "Swim Shorts",
  "Beard Groom Kit",
  "Shaving Kits",
  "Epilators",
  "Breast Pumps",
];

export function DeliveryHighlightMessage({
  product,
  configProps,
}: Pick<StandardProps, "product" | "configProps">) {
  const { variant, isSelected } = useVariant(product);
  const currentPrice = variant.price?.value;

  if (
    ensureIsActive(product) &&
    !hasAnyStock(product) &&
    product.variants?.length === 1
  ) {
    return null;
  }

  if (
    isInStoreOnly(variant) ||
    isAvailableToPreOrder(variant) ||
    isFutureRelease(variant)
  ) {
    return null;
  }

  const isOverFreeDeliveryThreshold =
    parseFloat(currentPrice ?? "0") >= FREE_DELIVERY_THRESHOLD;

  let isOverClickAndCollectThreshold =
    parseFloat(currentPrice ?? "0") >= FREE_CLICK_AND_COLLECT_THRESHOLD;

  if (!isSelected) {
    let variants;
    const hasColors = hasColours(product);

    if (hasColors) {
      variants = product.variants.filter(
        (productVariant: Variant) =>
          productVariant.differentiators?.colour ===
          variant.differentiators?.colour,
      );
    } else {
      variants = product.variants;
    }

    isOverClickAndCollectThreshold = !variants.some(
      (variant) =>
        parseFloat(("price" in variant && variant.price.value) || "0") <
        FREE_CLICK_AND_COLLECT_THRESHOLD,
    );
  }

  const is2ManDelivery = product.fulfilmentOptions?.available
    .map((option) => option.aliases.dmsId)
    .includes(TWO_MAN_DELIVERY_DMS_ID);

  const isClickAndCollectAvailable =
    !!product.fulfilmentOptions?.available.some(
      (option) => option.aliases.dmsId === CLICK_AND_COLLECT,
    );

  let message;
  if (!is2ManDelivery) {
    message = isOverFreeDeliveryThreshold
      ? "Free standard delivery"
      : "Free standard delivery over £50";
  } else {
    message = isOverFreeDeliveryThreshold
      ? "Free UK mainland delivery"
      : "Free UK mainland delivery over £50";
  }

  const showFreeReturns =
    typeof configProps?.showFreeReturns === "boolean" &&
    configProps.showFreeReturns;

  const showFreeReturnsWithRestrictionsAlways =
    typeof configProps?.showFreeReturnsWithRestrictionsAlways === "boolean" &&
    configProps.showFreeReturnsWithRestrictionsAlways;

  const showFreeReturnsWithRestrictions =
    showFreeReturnsWithRestrictionsAlways ||
    !!(
      product?.sellingProductType &&
      productTypesForRestrictedReturns.includes(product.sellingProductType)
    );

  return (
    <DeliveryHighlightMessageUI
      message={message}
      showExtendedReturnsMessage={isInExtendedReturnsWindow()}
      isClickAndCollectAvailable={isClickAndCollectAvailable}
      showClickAndCollect={
        configProps?.showClickAndCollect as Props["showClickAndCollect"]
      }
      isOverClickAndCollectThreshold={isOverClickAndCollectThreshold}
      showFreeReturns={showFreeReturns}
      showFreeReturnsWithRestrictions={showFreeReturnsWithRestrictions}
    />
  );
}
